import React, { useState, useRef, useEffect } from "react";
import {
  FaUser,
  FaBuilding,
  FaCalendarAlt,
  FaEllipsisV,
  FaEdit,
  FaCopy,
  FaTrashAlt,
  FaCheckCircle,
  FaExclamationCircle,
  FaPaperclip,
} from "react-icons/fa";

const TaskCard = ({ task, onEdit, onDuplicate, onDelete, onStatusChange }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  // Format Due Date
  const formatDueDate = (dateStr) => {
    if (!dateStr) return "No deadline";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  // Check if overdue
  const isOverdue = () => {
    if (!task.due_date || task.status === "Completed") return false;
    const due = new Date(task.due_date);
    const now = new Date();
    return due < now;
  };

  const getPriorityBadgeClass = (priority) => {
    const p = (priority || "Medium").toLowerCase();
    if (p === "urgent") return "tm-badge tm-badge-priority-urgent";
    if (p === "high") return "tm-badge tm-badge-priority-high";
    if (p === "low") return "tm-badge tm-badge-priority-low";
    return "tm-badge tm-badge-priority-medium";
  };

  const getStatusBadgeClass = (status) => {
    const s = (status || "To Do").toLowerCase();
    if (s.includes("progress")) return "tm-badge tm-badge-status-inprogress";
    if (s.includes("review")) return "tm-badge tm-badge-status-inreview";
    if (s.includes("complete")) return "tm-badge tm-badge-status-completed";
    return "tm-badge tm-badge-status-todo";
  };

  return (
    <div className="tm-card" onClick={() => onEdit(task)}>
      {/* Header with Title and Menu */}
      <div className="tm-card-header">
        <h4 className={`tm-card-title ${task.status === "Completed" ? "completed" : ""}`}>
          {task.title}
        </h4>

        <div style={{ position: "relative" }} ref={menuRef}>
          <button
            className="tm-card-menu-btn"
            title="Options"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(!menuOpen);
            }}
          >
            <FaEllipsisV />
          </button>

          {menuOpen && (
            <div className="tm-menu-dropdown" onClick={(e) => e.stopPropagation()}>
              <button
                className="tm-menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  onEdit(task);
                }}
              >
                <FaEdit /> Edit Task
              </button>

              <button
                className="tm-menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  onDuplicate(task);
                }}
              >
                <FaCopy /> Duplicate
              </button>

              {task.status !== "Completed" ? (
                <button
                  className="tm-menu-item"
                  onClick={() => {
                    setMenuOpen(false);
                    onStatusChange(task.id, "Completed");
                  }}
                >
                  <FaCheckCircle style={{ color: "#10b981" }} /> Mark Done
                </button>
              ) : (
                <button
                  className="tm-menu-item"
                  onClick={() => {
                    setMenuOpen(false);
                    onStatusChange(task.id, "In Progress");
                  }}
                >
                  <FaCheckCircle style={{ color: "#8b5cf6" }} /> Reopen
                </button>
              )}

              <button
                className="tm-menu-item delete"
                onClick={() => {
                  setMenuOpen(false);
                  onDelete(task.id);
                }}
              >
                <FaTrashAlt /> Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Body: Assigned To, Client, Deal */}
      <div className="tm-card-body">
        {task.assigned_to && (
          <div className="tm-card-meta-row">
            <FaUser />
            <span className="tm-card-meta-label">Assigned to:</span>
            <span className="tm-card-meta-value">{task.assigned_to}</span>
          </div>
        )}

        {task.client_name && (
          <div className="tm-card-meta-row">
            <FaBuilding />
            <span className="tm-card-meta-label">Client:</span>
            <span className="tm-client-badge">{task.client_name}</span>
          </div>
        )}
      </div>

      {/* Footer: Priority, Due Date, Status */}
      <div className="tm-card-footer">
        <span className={getPriorityBadgeClass(task.priority)}>
          {task.priority || "Medium"}
        </span>

        <span className={`tm-due-date ${isOverdue() ? "overdue" : ""}`}>
          {isOverdue() ? <FaExclamationCircle /> : <FaCalendarAlt />}
          {formatDueDate(task.due_date)}
        </span>

        <span className={getStatusBadgeClass(task.status)}>
          {task.status || "To Do"}
        </span>

        {task.attachments && task.attachments.length > 0 && (
          <span className="tm-attachment-badge" title={`${task.attachments.length} attachment(s)`}>
            <FaPaperclip /> {task.attachments.length}
          </span>
        )}
      </div>
    </div>
  );
};

export default TaskCard;
